/**
 * The SDMS open chain — from the template + the batch's records to the workbook the sheet shows —
 * with ONE ExcelJS parse and ONE write (speed, 2026-09-30).
 *
 * Both open paths of SdmsResultEntryPage (Retrieve / reopen of a saved batch, and the QC Batch → SDMS
 * handoff) run the same steps in the same order: mail merge → "…-Oriented Summary" fill → dept-chain,
 * Qualifier and ISReport dropdowns → attached calibration → raw data → the saved sheet's blob-only
 * values (reopen only) → the dilution ISReport rule. Each step used to parse the whole workbook and
 * write it back out; on a VOC batch that was ~13 s of the ~14 s before the sheet appeared. Here every
 * step edits the one in-memory workbook the merge produced (the `…ToWorkbook` / `…IntoWorkbook` forms
 * next to each step), and it is written once at the end. The steps, their order, their guards and
 * their error handling are exactly the page's; only the parse / write between them is gone.
 */
import type { SdmsDeptChainDropdown } from './qcBatch.api';
import { mergeSdmsTemplateWorkbook } from './sdmsMailMerge';
import { applySummaryOrientationToWorkbook } from './sdmsSummaryOrientation';
import { applyDeptChainDropdownsToWorkbook } from './sdmsDeptChainDropdown';
import { applyQualifierDropdownToWorkbook } from './sdmsQualifierDropdown';
import { applyIsReportDropdownToWorkbook } from './sdmsRuntimeDropdowns';
import { embedCalibrationSheetsIntoWorkbook } from './sdmsAttachCalibration';
import { carryRawDataIntoWorkbook, type RawDataPrefetch } from './sdmsAttachRawData';
import { overlayBlobOnlyValuesIntoWorkbook } from './sdmsBlobOnlyValues';
import { carrySavedRoundingIntoWorkbook } from './sdmsSavedRounding';
import { applyCalibrationFieldsToWorkbook } from './sdmsCalibrationFields';
import { loadSdmsWorkbook, normalizeCfNumFmts, settleAddedSheet } from './sdmsWorkbookIo';
import { feedSavedValuesToDefaultCells } from './sdmsSavedDefaultCells';

type Row = Record<string, unknown>;

/** The progress phases the page shows while a sheet opens (after 'fetch'). */
export type OpenChainPhase = 'merge' | 'summaries' | 'dropdowns' | 'calibration' | 'rawdata' | 'saved-values' | 'dilution';

/** The attached calibration's own merge data (qcBatchApi.sdmsCalibrationOpen). */
export interface OpenChainCalibration {
  template: ArrayBuffer;
  records: Row[];
  traceability: string | null;
  model: number;
}

export interface OpenChainInput {
  template: ArrayBuffer;
  header: Row;
  records: Row[];
  reagents: Row[];
  instruments: Row[];
  /** OUT from the merge (where each header FIELD landed); the dropdown steps read QUALIFIER / ISREPORT. */
  fieldRefs: Record<string, string>;
  traceability: string | null;
  model: number;
  entryFields: readonly string[] | null;
  /** Continuous fields placed in a header cell (merge-data continuousHeaderFields) — those cells take the batch header only. */
  continuousHeaderFields?: readonly string[] | null;
  fieldCells: Record<string, string>;
  testName?: string;
  deptChainDropdown?: SdmsDeptChainDropdown | null;
  /** Dept-chain default pick: the batch creator (reopen) or the current user (handoff). */
  creator: string;
  /** The controlled qualifier symbols — pass the request started with the other fetches so it overlaps the merge. */
  qualifierSymbols: Promise<string[]> | string[];
  /** The attached calibration to re-embed (started early), or null when the batch has none. */
  calibration?: Promise<OpenChainCalibration | null> | null;
  /** OG CalibAndABIDSupport: the Calibration sheet's identity fields — cells (field -> A1, merge-data
   *  calibrationCells) and their values (CALIBRATIONID = the batch's calibration, CALIBRATEDBY = the user). */
  calibrationFields?: { cells: Record<string, string> | null; values: Record<string, string | null> } | null;
  /** Header the calibration merges with (TEST / METHOD / MATRIX / CALIBRATIONID). */
  calibrationHeader?: Row;
  /** The saved batch whose raw data is carried; null skips the step (a new batch has none). */
  rawDataBatchOid?: string | null;
  /** Its raw-data downloads already started with the batch's data fetch (prefetchRawDataCarry); absent = here. */
  rawDataPrefetch?: Promise<RawDataPrefetch | null> | null;
  /** The batch's run order (SysSampleCodes, first appearance) for the multi-instrument summary. */
  sequence: readonly string[];
  /** The batch's saved sheet for the blob-only values (started early); null skips the step (handoff). */
  savedSheet?: Promise<{ buf: ArrayBuffer; source?: string } | null> | null;
  /** That saved sheet already READ (started with the batch's data fetch, so its download and read run while
   *  the server prepares the merge data); null / absent = read it here. */
  savedWorkbook?: Promise<import('exceljs').Workbook | null> | null;
  /** Called after each phase (the page's progress bar + per-phase timing). */
  step?: (phase: OpenChainPhase) => Promise<void> | void;
  /** True once the page no longer wants the result (an attached calibration is not merged then). */
  cancelled?: () => boolean;
}

export interface OpenChainResult {
  /** The workbook's bytes — the sheet's base for Save / import. Written WITHOUT the CF `text=` repair: nothing
   *  stores these bytes as they are (Save goes through getEditedXlsx and an import refresh through
   *  writeSdmsWorkbook, both of which repair), and neither Univer nor ExcelJS reads that attribute — the sheet
   *  maps these bytes exactly as it mapped the repaired ones (proven on 102 batches, 2026-09-30). */
  buf: ArrayBuffer;
  /** True when the attached calibration's sheet(s) were embedded. */
  calibrationAttached: boolean;
}

/** Build the opened sheet's workbook. Throws only where the page's chain threw (the merge, the summary
 *  fill and the dropdowns) — the page then falls back to the saved blob / the raw sequence, as before. */
export async function buildSdmsOpenWorkbook(inp: OpenChainInput): Promise<OpenChainResult> {
  const step = async (phase: OpenChainPhase) => { await inp.step?.(phase); };
  // A saved pick in a cell the template seeds with FIELD("DEFAULTRESULT") (Colilert-18 P/A Result J11) comes back
  // instead of the default — see sdmsSavedDefaultCells. Read from the template's main sheet (one small parse).
  const tplMain = (await loadSdmsWorkbook(inp.template)).worksheets[0];
  const records = tplMain ? feedSavedValuesToDefaultCells(tplMain, inp.records, inp.fieldCells) : inp.records;
  const merged = await mergeSdmsTemplateWorkbook(inp.template, inp.header, records, inp.reagents, inp.instruments,
    inp.fieldRefs, inp.traceability, inp.model, inp.entryFields, inp.continuousHeaderFields ?? null);
  // The ONE round trip kept: the merge leaves the model in a state only a write + re-read settles (ExcelJS
  // Row.splice — the trim right of DETAILRANGE — keeps emptied cells with their old spans, and a sheet the
  // merge builds carries no sheet-format defaults). Every later step read the merge's WRITTEN output, so
  // it must see that settled model too — the equivalence harness finds row / style differences without it.
  // (A plain write: the CF `text=` repair of writeSdmsWorkbook is skipped here because ExcelJS's reader
  // ignores that attribute — the final write restores it. And an UNCOMPRESSED one: these bytes are only read
  // straight back, so zip compression was pure cost — ~0.3 s of deflate + inflate on a VOC batch. The model
  // read back is the same either way; compression is the zip container's, not the workbook's. 2026-09-30.)
  const wb = await loadSdmsWorkbook((await merged.xlsx.writeBuffer({ zip: { compression: 'STORE' } })) as ArrayBuffer);
  await step('merge');
  // Sheets a step ADDS (the hidden Qualifier / AnalystList lists, the calibration copies, Raw Data) are
  // written straight from memory here; the step-by-step chain re-read them at the next step that wrote the
  // workbook, and ExcelJS's reader fills print defaults in (see settleAddedSheet). `wrote(true)` marks a step
  // the old chain would have written: every sheet added BEFORE it is settled, its own additions wait.
  let pending: Worksheet[] = [];
  const added = (before: ReadonlySet<Worksheet>, didWrite: boolean) => {
    if (didWrite) { pending.forEach(settleAddedSheet); pending = []; }
    for (const ws of wb.worksheets) if (!before.has(ws)) pending.push(ws);
  };
  const sheets = () => new Set(wb.worksheets);
  let before: Set<Worksheet>;

  // "…-Oriented Summary" sheets — OG's post-merge pivot fill, Traceability-gated (no-op otherwise).
  before = sheets();
  added(before, applySummaryOrientationToWorkbook(wb, {
    records: inp.records, traceability: inp.traceability, model: inp.model, fieldCells: inp.fieldCells, testName: inp.testName,
  }));
  await step('summaries');
  // Dept-chain analyst dropdown (OG SetUserDropdownsFromDepartmentChain), the Qualifier list (OG runtime
  // DataValidations.Add) and the ISReport True/False list — each a no-op when the template has no such field.
  before = sheets();
  added(before, applyDeptChainDropdownsToWorkbook(wb, inp.deptChainDropdown, inp.creator));
  const symbols = await inp.qualifierSymbols;
  before = sheets();
  added(before, applyQualifierDropdownToWorkbook(wb, inp.fieldRefs.QUALIFIER, symbols));
  before = sheets();
  added(before, applyIsReportDropdownToWorkbook(wb, inp.fieldRefs.ISREPORT));
  await step('dropdowns');

  // Attach Calibration: re-merge the linked calibration from its tables and copy its sheet(s) in.
  let calibrationAttached = false;
  if (inp.calibration) {
    try {
      const cmd = await inp.calibration;
      if (cmd && !inp.cancelled?.()) {
        const cal = await mergeSdmsTemplateWorkbook(cmd.template, inp.calibrationHeader ?? {}, cmd.records, [], [],
          inp.fieldRefs, cmd.traceability, cmd.model);
        before = sheets();
        embedCalibrationSheetsIntoWorkbook(wb, cal, cmd.model);
        added(before, true);
        calibrationAttached = true;
      }
    } catch (calErr) { console.warn('[SDMS] calibration re-embed failed', calErr); }
    await step('calibration');
  }

  // Raw Data carry (OG ReAttachRawData): the batch's imported instrument raw data as the "Raw Data" sheet.
  if (inp.rawDataBatchOid) {
    try {
      before = sheets();
      added(before, await carryRawDataIntoWorkbook(wb, inp.rawDataBatchOid, inp.sequence, inp.rawDataPrefetch));
    } catch (rdErr) { console.warn('[SDMS] raw-data carry failed', rdErr); }
    await step('rawdata');
  }

  // Blob-only analyst values (owner decision D2, 2026-09-26) from the batch's SAVED sheet — reopen only.
  // X-Sdms-Source is not in the CORS exposed headers, so a cross-origin read sees ""; only an explicit
  // "template" is skipped (overlaying the bare template changes nothing anyway).
  if (inp.savedSheet) {
    try {
      const saved = await inp.savedSheet;
      if (saved && saved.source !== 'template') {
        // The template and saved sheet are read ONCE here for both steps below (each used to read both itself).
        const [tplWb, savedWb] = await Promise.all([loadSdmsWorkbook(inp.template),
          (inp.savedWorkbook ?? Promise.resolve(null)).then(w => w ?? loadSdmsWorkbook(saved.buf))]);
        const parsedSaved = { tpl: tplWb, saved: savedWb };
        before = sheets();
        added(before, (await overlayBlobOnlyValuesIntoWorkbook(wb, inp.template, saved.buf, inp.model,
          inp.continuousHeaderFields ?? null, parsedSaved)) > 0);
        // The Save's rounding (`_LIMSRound` wraps + formats) lives only in the saved sheet: carry it, so a reopened
        // batch shows the rounded Rec % 107 its tables hold, not the template's raw 106.7 (sdmsSavedRounding.ts).
        try {
          before = sheets();
          added(before, (await carrySavedRoundingIntoWorkbook(wb, inp.template, saved.buf, inp.model, parsedSaved)) > 0);
        } catch (rdErr) { console.warn('[SDMS] saved rounding carry failed', rdErr); }
      }
    } catch (boErr) { console.warn('[SDMS] blob-only values failed', boErr); }
    await step('saved-values');
  }

  // Dilution IsReport is NOT applied on load (owner decision 2026-10-22, OG SpreadsheetEntryController :5351): the
  // sheet shows the batch's own flags — the sequence's defaults on a fresh batch, the SAVED values on a reopen — so an
  // IsReport the analyst set and saved stays. The rule runs after an import and from the right-click menu only.
  // (The 'dilution' phase stays so the progress steps are unchanged.)
  await step('dilution');

  // Calibration ID / Calibrated By on the Calibration sheet (OG CalibAndABIDSupport), after the saved sheet's
  // values so the batch's own calibration wins over a stale ID. A no-op without a Calibration sheet.
  if (inp.calibrationFields) {
    try {
      before = sheets();
      added(before, applyCalibrationFieldsToWorkbook(wb, inp.calibrationFields.cells, inp.calibrationFields.values) > 0);
    } catch (cfErr) { console.warn('[SDMS] calibration fields failed', cfErr); }
  }

  // Speed (2026-09-30): the CF repair is left to the paths that store the bytes (see OpenChainResult.buf).
  // NOT done: mapping this in-memory workbook instead of parsing the bytes back — after the steps above it is not
  // what a fresh read gives (dropdown formulas, a trailing row, a column width, a merge, a style differed on 62 of
  // 102 batches), so the sheet keeps parsing the bytes.
  normalizeCfNumFmts(wb);   // a CF number format written as an object stops ALPACA opening the batch
  return { buf: (await wb.xlsx.writeBuffer()) as ArrayBuffer, calibrationAttached };
}

type Worksheet = import('exceljs').Worksheet;
